import React, { useCallback, useMemo, useState, useEffect } from "react";
import { Box, Divider, CircularProgress, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { AppUtils } from "../../models/common/appUtils";
import { calculatedWorkHours } from "../../store/workHoursSlice";
import { useAsyncThunkAction } from "../../hooks/useAsyncThunkAction";
import {
    useSelectCalculatedPaidWorkHoursAmountByDate,
    useSelectCalculatedWorkHoursAmountBySalaryRowTypeId,
} from "../../hooks/workTime/useSelectWorkHours";
import { useSelectSalaryPeriodByIdAndDate } from "../../hooks/workTime/useSelectSalaryPeriod";
import AccordionGroup, { AccordionDetailsItem } from "../../framework/muiAccordion";
import { useAppDispatch, useAppSelector } from "../../framework/customStore";
import {
    additionCategories,
    normalWorkPaidCategories,
    overtimeCategories,
} from "../../models/workTime/hourBookingCategory";
import { handleApiError } from "../../models/store/storeEffects";
import dayjs, { Dayjs } from "dayjs";


interface WorkLogMainProps {
    employeeId: string;
    salaryPeriodTypeId: string;
}

export const WorklogMain: React.FC<WorkLogMainProps> = ({ employeeId, salaryPeriodTypeId }) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const [loading, setLoading] = useState(true);
    const [dataFetched, setDataFetched] = useState(false);

    const dateToShortDateStr = (date: string | Dayjs | Date | number): string => {
        return dayjs(date).format("D.M.");
    };

    const dateToDateStr = (date: string | Dayjs | Date | number): string => {
        return dayjs(date).format("D.M.YYYY");
    };

    const dateToDateJSON = (date?: string | Dayjs | Date | number): string => {
        return dayjs(date).format("YYYY-MM-DD");
    };

    const dateString = dateToDateJSON();

    const salaryRowTypes = useAppSelector(
        (state) => state.workShiftTimeSlot.salaryRowTypes
    );

    const selectedSalaryPeriodItem = useSelectSalaryPeriodByIdAndDate(salaryPeriodTypeId, dateString);

    const salaryPeriodWorkHours =
        useSelectCalculatedWorkHoursAmountBySalaryRowTypeId(
            employeeId,
            selectedSalaryPeriodItem?.startDate,
            selectedSalaryPeriodItem?.endDate
        );

    const salaryPeriodMinutes = useSelectCalculatedPaidWorkHoursAmountByDate(
        employeeId,
        selectedSalaryPeriodItem?.startDate,
        selectedSalaryPeriodItem?.endDate
    );

    const salaryRowTypeMap = useMemo(
        () => new Map((salaryRowTypes ?? []).map((s) => [s.id, s])),
        [salaryRowTypes]
    );

    const getSectionContent = useCallback(
        (workHours: { [k: string]: number }) => {
            const categories = [
                normalWorkPaidCategories(),
                additionCategories(),
                overtimeCategories(),
            ];

            const grouped: { [key: number]: JSX.Element[] } = {};

            const srts = Object.keys(workHours)
                .map((k) => salaryRowTypeMap.get(k))
                .filter(Boolean);

            srts.forEach((srt) => {
                let idx = categories.findIndex((c) =>
                    c.some((cat) => srt.hourBookingCategories.includes(cat))
                );

                // If idx not found, it will be in last group.
                idx = idx === -1 ? categories.length : idx;
                grouped[idx] ||= [];

                // Group items by idx
                // Push each item to their group
                grouped[idx].push(
                    <AccordionDetailsItem
                        key={srt.id}
                        title={srt.name}
                        hours={AppUtils.getDisplayedNumberBasedOnUnit(
                            workHours[srt.id],
                            srt.measureUnit
                        )}
                    />
                );
            });

            let retVal: JSX.Element[] = [];

            // Return each group separated by Divider
            Object.keys(grouped)
                .sort()
                .forEach((k, i) => {
                    const arr = grouped[k];

                    if (arr.length > 0) {
                        if (retVal.length > 0) {
                            retVal.push(
                                <Divider
                                    key={`divider-${i}`}
                                    sx={{ mt: 1.5, mb: 0.5 }}
                                />
                            );
                        }

                        retVal = [...retVal, ...arr];
                    }
                });

            return retVal;
        },
        [salaryRowTypeMap]
    );

    useAsyncThunkAction(
        () => {
            if (selectedSalaryPeriodItem?.id) {
                return calculatedWorkHours({
                    employeeId: employeeId,
                    salaryPeriodId: selectedSalaryPeriodItem?.id,
                    startDate: selectedSalaryPeriodItem?.startDate,
                    endDate: selectedSalaryPeriodItem?.endDate,
                });
            }
        },
        {
            onError: (e) => {
                handleApiError(e, dispatch);
            },
            onSuccess: () => {
                setLoading(false);
                setDataFetched(true);
            },
        },
        [selectedSalaryPeriodItem?.id]
    );

    useEffect(() => {
        if (dataFetched) {
            setLoading(false);
        }
    }, [dataFetched]);

    const salaryPeriodTitle = useMemo(
        () =>
            `${t("salaryPeriod")} (${
                new Date(selectedSalaryPeriodItem?.startDate).getFullYear() !==
                new Date(selectedSalaryPeriodItem?.endDate).getFullYear()
                    ? dateToDateJSON(selectedSalaryPeriodItem?.startDate)
                    : dateToShortDateStr(
                          selectedSalaryPeriodItem?.startDate
                      )
            } -
                ${dateToDateStr(selectedSalaryPeriodItem?.endDate)})`,
        [
            selectedSalaryPeriodItem?.startDate,
            selectedSalaryPeriodItem?.endDate,
            t,
        ]
    );

    const dataFound = useAppSelector(
        (state) => {
            const calculatedWorkHours = state.workHours.calculated.find(item => item.employeeId === employeeId);
            return !!calculatedWorkHours;
        }
    );

    if (!selectedSalaryPeriodItem) {
        return (
            <Box sx={{ p: 1 }}>
                <Typography variant="h6" color="primary">
                    {t("monitorView.worklogNoSalaryPeriod")}
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 1 }}>
            {loading ? (
                <CircularProgress />
            ) : !dataFound && dataFetched ? (
                <Typography variant="h6" color="primary">
                    {t("monitorView.worklogNoData")}
                </Typography>
            ) : (
                <AccordionGroup
                    items={[
                        selectedSalaryPeriodItem && salaryPeriodMinutes
                            ? {
                                header: salaryPeriodTitle,
                                subheader:
                                    AppUtils.getHoursAndMinutesByDurationMin(
                                        salaryPeriodMinutes,
                                        true
                                    ),
                                content: getSectionContent(
                                    salaryPeriodWorkHours
                                ),
                            }
                            : null,
                    ].filter(Boolean)}
                />
            )}
        </Box>
    );
};